<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { roleCode, session } from '../../session'
import { useI18n } from '../../i18n'
import { systemRoleName } from '../../access/labels.js'
import { can } from '../../access/permissions.js'

const props = defineProps({ user: { type: Object, default: null }, busy: { type: Boolean, default: false } })
const emit = defineEmits(['close', 'toggle', 'remove'])
const router = useRouter()
const canManage = computed(() => roleCode() === 'ADMIN' && props.user?.id !== session.user?.id)
const canViewAudit = computed(() => can(session.user, 'audit.read'))
const { locale, t } = useI18n()
const pane = ref(null)
const mobileModal = ref(false)
let mediaQuery = null
let previousFocus = null
let overlayActive = false

function focusableItems() {
  if (!pane.value) return []
  return [...pane.value.querySelectorAll('button,[href],[tabindex]:not([tabindex="-1"])')]
    .filter((item) => !item.disabled)
}

async function activateOverlay() {
  if (!mobileModal.value || !props.user || overlayActive) return
  overlayActive = true
  previousFocus = document.activeElement
  document.body.classList.add('drawer-open')
  await nextTick()
  pane.value?.focus()
}

function deactivateOverlay(restoreFocus = true) {
  if (!overlayActive) return
  overlayActive = false
  document.body.classList.remove('drawer-open')
  if (restoreFocus) previousFocus?.focus?.()
  previousFocus = null
}

function mediaChanged(event) {
  mobileModal.value = event.matches
  if (event.matches) activateOverlay()
  else deactivateOverlay()
}

function keydown(event) {
  if (!mobileModal.value) return
  if (event.key === 'Escape') {
    event.preventDefault()
    emit('close')
    return
  }
  if (event.key !== 'Tab') return
  const items = focusableItems()
  if (!items.length) return
  const first = items[0]
  const last = items[items.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function dateTime(value) {
  const code = ({ uk: 'uk-UA', ru: 'ru-RU', en: 'en-GB' })[locale.value] ?? 'uk-UA'
  return value ? new Date(value).toLocaleString(code) : t('neverSignedIn')
}

function edit() {
  router.push({ name: 'access-user-edit', params: { userId: props.user.id } })
}

function viewAudit() {
  router.push({ name: 'admin-audit-log', query: { actor: props.user.username } })
}

watch(() => props.user?.id, (userId) => {
  if (userId) activateOverlay()
  else deactivateOverlay(false)
})

onMounted(() => {
  mediaQuery = window.matchMedia('(max-width: 1180px)')
  mobileModal.value = mediaQuery.matches
  mediaQuery.addEventListener('change', mediaChanged)
  activateOverlay()
})

onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', mediaChanged)
  deactivateOverlay()
})
</script>

<template>
  <aside v-if="user" ref="pane" class="access-detail-pane" :role="mobileModal ? 'dialog' : undefined"
    :aria-modal="mobileModal ? 'true' : undefined" :aria-label="t('userDetails')" tabindex="-1" @keydown="keydown">
    <button class="access-pane-close" type="button" :aria-label="t('close')" @click="emit('close')">×</button>
    <header><span class="access-avatar large">{{ (user.displayName || user.username).slice(0, 2).toUpperCase() }}</span>
      <div><h2>{{ user.displayName }}</h2><p class="account-status" :class="user.enabled ? 'enabled' : 'blocked'"><i></i>{{ user.enabled ? t('userActive') : t('userBlocked') }}</p></div></header>
    <section><h3>{{ t('account') }}</h3><dl><dt>{{ t('username') }}</dt><dd>{{ user.username }}</dd><dt>{{ t('createdAt') }}</dt><dd>{{ dateTime(user.createdAt) }}</dd><dt>{{ t('lastSignIn') }}</dt><dd>{{ dateTime(user.lastLoginAt) }}</dd></dl></section>
    <section><h3>{{ t('role') }}</h3><span class="role-chip">{{ systemRoleName(t, user.role) }}</span><p v-if="user.role.isSystem" class="muted">{{ t('systemRoleFixedHint') }}</p></section>
    <section><h3>{{ t('stationAccess') }}</h3><strong>{{ user.stationPolicy === 'ALL' ? t('allStationsFuture') : t('stationCount', { count: user.stationCount }) }}</strong></section>
    <footer v-if="roleCode() === 'ADMIN' || canViewAudit">
      <button v-if="canViewAudit" class="button button-secondary" type="button" @click="viewAudit">{{ t('viewUserAudit') }}</button>
      <button v-if="roleCode() === 'ADMIN'" class="button button-secondary" type="button" @click="edit">{{ t('editAccess') }}</button>
      <button v-if="canManage" class="button button-secondary" type="button" :disabled="busy" @click="emit('toggle', user)">{{ user.enabled ? t('block') : t('unblock') }}</button>
      <button v-if="canManage" class="button button-danger" type="button" :disabled="busy" @click="emit('remove', user)">{{ t('delete') }}</button>
      <p v-if="roleCode() === 'ADMIN' && !canManage" class="muted">{{ t('currentAccountProtected') }}</p>
    </footer>
    <p v-if="roleCode() !== 'ADMIN'" class="notice info">{{ t('accessReadOnly') }}</p>
  </aside>
</template>
